import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import { isNumber } from 'lodash';

import { useToast } from '@redshelf/redshelf-ui';

import {
  BookValuesContext,
  NetworkStatusValuesContext,
  PrintValuesContext,
  PrintModifiersContext,
} from 'contexts';
import { UserContentPrintJob } from 'types';
import { PrintToast, ToastPrintButton } from 'components/PrintMenu/components';

import PRINT_HISTORY_QUERY from 'graphql/queries/PrintHistoryQuery.graphql';
import SINGLE_PRINT_JOB_QUERY from 'graphql/queries/SinglePrintJobQuery.graphql';
import CREATE_PRINT_JOB_MUTATION from 'graphql/mutations/print/CreatePrintJob.graphql';
import CANCEL_PRINT_JOB_MUTATION from 'graphql/mutations/print/CancelPrintJob.graphql';
import { getAllPagesInQueue } from 'utilities/printUtilities';
import { getSuffix } from 'utilities';

const NEW_PRINT_JOB_POLL_INTERVAL = 2000;
const ERROR_STATUS = {
  POLLING: 'POLLING',
  MUTATION: 'MUTATION',
};

const createRangeForCurrentSelection = ({
  pageRangeStart,
  pageRangeEnd,
  pages,
}) => {
  if (!isNumber(pageRangeEnd) || !isNumber(pageRangeStart)) {
    return [];
  }

  return pages.filter(
    (page) => page.index >= pageRangeStart && page.index <= pageRangeEnd,
  );
};

const getPreviouslyPrintedPages = (printJobs) => {
  const summedTotalPages = printJobs.reduce(
    /* Bug in the BE when calcing number of pages == 1, so use array */
    (a, c) => {
      const allPages = [...a, ...c.pageNumbers];
      return [...new Set(allPages)];
    },
    [],
  );
  return summedTotalPages;
};

const getLabelForAllFromPageRange = (pageRangesQueue) => {
  return pageRangesQueue.reduce((acc, pageRange) => {
    const currentLabel = getLabelFromPageRange(pageRange.pages);

    if (currentLabel) {
      const punctuation = acc ? ', ' : '';
      acc += punctuation + currentLabel;
    }

    return acc;
  }, '');
};

const getLabelFromPageRange = (range) => {
  const { length } = range;
  if (length === 0) {
    return '';
  }
  if (length === 1) {
    return range[0].label;
  } else {
    return `${range[0].label} - ${range[length - 1].label}`;
  }
};

/**
 * The PrintProvider holds the logic, state and queries/mutations for print.
 * It keeps track of Print Queue, Print History and Toasts.
 * It fetches the data for Print History on the initial render.
 * When the onPrint function is called a mutation for a new print job is executed
 * upon completion of the mutation a Lazy Query starts polling for the new print job
 * until it is either successful or failed.
 * Once successful, the Print History query is re-fetched to update its data.
 */
export const PrintProvider = ({ children }) => {
  const { book } = useContext(BookValuesContext);
  const { isOffline } = useContext(NetworkStatusValuesContext);
  const epubId = useMemo(() => book?.pk, [book]);
  const pages = useMemo(() => book?.pages ?? [], [book]);

  const { enqueueToast, closeToast } = useToast();

  const [printHistory, setPrintHistory] = useState([]);
  /* Array of the indexes of the pages that have already been printed */
  const [pagesAlreadyPrinted, setPagesAlreadyPrinted] = useState([]);
  /* Newly created Print job -- used to feed into toast */
  const [newPrintPk, setNewPrintPk] = useState(null);
  const [newPrintId, setNewPrintId] = useState(null);
  /* used to make sure we don't show the success toast twice */
  const [successToastShown, setSuccessToastShown] = useState(null);

  // controls if the error dialog is open
  const [hasPrintError, setHasPrintError] = useState(false);
  // used to keep track of what part of the workflow needs to be retried
  const [errorStatus, setErrorStatus] = useState(null);
  // keeps the last print job data in case we have to retry the mutation
  const [retryErrorData, setRetryErrorData] = useState(null);
  const [hasClosedPrintDialog, setHasClosedPrintDialog] = useState(false);
  const [isCancelPrintDialogOpen, setIsCancelPrintDialogOpen] = useState(false);
  const [isCurrentPrintJobCanceled, setIsCurrentPrintJobCanceled] =
    useState(false);
  const canOpenCancelPrintDialogRef = useRef(null);

  const [pageRangeStart, setPageRangeStart] = useState('');
  const [pageRangeEnd, setPageRangeEnd] = useState('');

  const [pageRangesQueue, setPageRangesQueue] = useState([]);
  const [shouldPrintUserContent, setShouldPrintUserContent] = useState(false);
  const displayText = getLabelForAllFromPageRange(pageRangesQueue);

  // ---> Fetch queries, toast logic
  const { data: historyData, refetch: refetchPrintHistory } = useQuery(
    PRINT_HISTORY_QUERY,
    {
      variables: { epubId },
    },
  );

  useEffect(() => {
    const printJobs = historyData?.printJobsByEpub?.map(
      (printJob) => new UserContentPrintJob(pages, printJob),
    );

    const filteredJobs =
      printJobs?.filter((job) => job.shouldDisplayOnHistory) ?? [];
    setPrintHistory(filteredJobs);

    const previouslyPrintedPages = getPreviouslyPrintedPages(filteredJobs);
    setPagesAlreadyPrinted(previouslyPrintedPages);
  }, [historyData, pages]);

  const [
    pollNewPrintJob,
    {
      data: newPrintJobData,
      error: newPrintJobError,
      loading: newPrintJobLoading,
      stopPolling: stopNewPrintJobPolling,
    },
  ] = useLazyQuery(SINGLE_PRINT_JOB_QUERY, {
    variables: { pk: newPrintPk },
    pollInterval: NEW_PRINT_JOB_POLL_INTERVAL,
    notifyOnNetworkStatusChange: true,
  });

  const [createPrintJob] = useMutation(CREATE_PRINT_JOB_MUTATION, {
    /* On return of print job mutation, poll new printjob and show toast */
    onCompleted: (data) => {
      const { pk: newPk, id, totalPages } = data?.createPrintJob?.printJob;
      setSuccessToastShown(null);
      setIsCurrentPrintJobCanceled(false);
      canOpenCancelPrintDialogRef.current = true;

      setNewPrintPk(newPk);
      setNewPrintId(id);
      pollNewPrintJob();

      const suffix = getSuffix(totalPages ?? retryErrorData.totalPages);
      enqueueToast(
        <PrintToast
          suffix={suffix}
          labelText={
            pageRangesQueue?.[0] ? displayText : retryErrorData.displayText
          }
          onCancelPrintClick={() =>
            canOpenCancelPrintDialogRef.current &&
            setIsCancelPrintDialogOpen(true)
          }
        />,
        {
          persist: true,
          key: `print-${newPk}`,
        },
      );

      setPageRangesQueue([]);
    },
    onError: () => {
      setHasPrintError(true);
      setErrorStatus(ERROR_STATUS.MUTATION);
    },
  });
  // Opens the dialog in case we have an error during polling
  useEffect(() => {
    // if the print job is loading we might have error from the previous query
    if (Boolean(newPrintJobError) && !newPrintJobLoading) {
      setHasPrintError(true);
      setErrorStatus(ERROR_STATUS.POLLING);

      stopNewPrintJobPolling();
      closeToast(`print-${newPrintPk}`);
    }
  }, [
    newPrintJobError,
    closeToast,
    stopNewPrintJobPolling,
    newPrintPk,
    newPrintJobLoading,
  ]);

  const [cancelPrintJob] = useMutation(CANCEL_PRINT_JOB_MUTATION, {
    variables: { printJobId: newPrintId },
    onCompleted: (data) => {
      stopNewPrintJobPolling();
      setIsCurrentPrintJobCanceled(true);
      closeToast(`print-${data.cancelPrintJob.printJob.pk}`);
    },
  });

  /* When new print job is complete, stop polling, show toast and refetch history */
  useEffect(() => {
    if (
      newPrintJobData?.printJobByPk?.state === 'complete' &&
      successToastShown !== newPrintJobData.printJobByPk.displayText &&
      newPrintJobData.printJobByPk.displayText !== ''
    ) {
      const { displayText, pk, totalPages } =
        newPrintJobData?.printJobByPk ?? {};

      const suffix = getSuffix(totalPages);
      if (
        !isCancelPrintDialogOpen &&
        !newPrintJobData.printJobByPk.canceled &&
        !isCurrentPrintJobCanceled
      ) {
        canOpenCancelPrintDialogRef.current = false;

        closeToast(`print-${pk}`);
        setSuccessToastShown(displayText);
        enqueueToast(
          <>
            Your print of page{suffix}{' '}
            <span className="noTranslate">{displayText} </span>
            is ready to print.
          </>,
          {
            variant: 'success',
            title: 'Success!',
            action: <ToastPrintButton pk={pk} />,
          },
        );

        refetchPrintHistory();
        clearAllSelections();
        setIsCancelPrintDialogOpen(false);
      }

      stopNewPrintJobPolling();

      // clean up the error data as this query completed successfully
      setRetryErrorData(null);
    }

    if (newPrintJobData?.printJobByPk?.state === 'failed') {
      stopNewPrintJobPolling();
      closeToast(`print-${newPrintJobData.printJobByPk.pk}`);

      setHasPrintError(true);
      setErrorStatus(ERROR_STATUS.MUTATION);
    }
  }, [
    newPrintJobData,
    closeToast,
    isCancelPrintDialogOpen,
    isCurrentPrintJobCanceled,
  ]);

  // Error Dialog logic --->
  // used to retry the print action
  // saved error data === the workflow didn't complete successfully for the user
  // executes different parts of the workflow depending on where the error occurred
  const retryPrintAction = useCallback(() => {
    if (retryErrorData) {
      if (errorStatus === ERROR_STATUS.MUTATION) {
        createPrintJob({
          variables: {
            input: retryErrorData,
          },
        });
      }
      if (errorStatus === ERROR_STATUS.POLLING) {
        pollNewPrintJob();
        const suffix = getSuffix(retryErrorData.totalPages);
        enqueueToast(
          <PrintToast
            suffix={suffix}
            labelText={retryErrorData.displayText}
            onCancelPrintClick={() =>
              canOpenCancelPrintDialogRef.current &&
              setIsCancelPrintDialogOpen(true)
            }
          />,
          {
            persist: true,
            key: `print-${newPrintPk}`,
          },
        );
      }
    }
  }, [
    retryErrorData,
    errorStatus,
    createPrintJob,
    pollNewPrintJob,
    enqueueToast,
    newPrintPk,
  ]);

  // if we go back online with a print error & a closed print error dialog
  useEffect(() => {
    if (!isOffline && !hasPrintError && hasClosedPrintDialog) {
      retryPrintAction();
      setHasClosedPrintDialog(false);
    }
  }, [isOffline, hasPrintError, retryPrintAction, hasClosedPrintDialog]);

  const closeErrorDialog = useCallback(() => {
    setHasPrintError(false);
    // make sure we only set that the dialog is closed while offline
    // in order to avoid constant error dialogs for the user if the query keeps failing when back online
    isOffline && setHasClosedPrintDialog(true);
  }, [isOffline]);
  // <--- Error Dialog logic
  // <--- Fetch queries, toast logic

  // Add and Remove from PrintQueue --->
  const addPageRange = useCallback(() => {
    if (
      pageRangesQueue.some(
        (pageRange) => pageRange?.id === `${pageRangeStart}-${pageRangeEnd}`,
      )
    ) {
      setPageRangeStart('');
      setPageRangeEnd('');
      return;
    }

    const selectedPageRanges = createRangeForCurrentSelection({
      pageRangeStart,
      pageRangeEnd,
      pages,
    });

    const labelForPageRange = getLabelFromPageRange(selectedPageRanges);

    setPageRangesQueue((currentPageRanges) => {
      const pages = [
        ...currentPageRanges,
        {
          id: `${pageRangeStart}-${pageRangeEnd}`,
          range: [pageRangeStart, pageRangeEnd],
          displayText: labelForPageRange,
          pages: selectedPageRanges,
        },
      ];

      // sort by first page first and then by second
      const sortedPages = [
        ...pages.sort((a, b) => {
          return a.range[0] - b.range[0] || a.range[1] - b.range[1];
        }),
      ];

      return sortedPages;
    });

    setPageRangeStart('');
    setPageRangeEnd('');
    return labelForPageRange;
  }, [pages, pageRangeEnd, pageRangesQueue, pageRangeStart]);

  const deleteQueue = useCallback((id) => {
    setPageRangesQueue((currentPageRanges) =>
      currentPageRanges.filter((pageRange) => pageRange.id !== id),
    );
  }, []);
  // <--- Add and Remove from PrintQueue

  // ---> Print and Cancel actions
  const onPrint = useCallback(() => {
    const pageNumbers = getAllPagesInQueue(pageRangesQueue);
    setRetryErrorData({
      epubId: epubId,
      pageNumbers: pageNumbers,
      displayText: displayText,
      shouldPrintAnnotations: shouldPrintUserContent,
    });

    createPrintJob({
      variables: {
        input: {
          epubId: epubId,
          pageNumbers: pageNumbers,
          displayText: displayText,
          shouldPrintAnnotations: shouldPrintUserContent,
        },
      },
    });
  }, [
    createPrintJob,
    displayText,
    epubId,
    pageRangesQueue,
    shouldPrintUserContent,
  ]);

  const clearAllSelections = useCallback(() => {
    setPageRangeStart('');
    setPageRangeEnd('');
    setPageRangesQueue([]);
    setShouldPrintUserContent(false);
  }, []);
  // <--- Print and Cancel actions

  const values = useMemo(
    () => ({
      displayText,
      printHistory,
      pagesAlreadyPrinted,

      shouldPrintUserContent,

      pageRangesQueue,
      pageRangeStart,
      pageRangeEnd,

      hasPrintError,
      isCancelPrintDialogOpen,
    }),
    [
      displayText,
      printHistory,
      pagesAlreadyPrinted,
      shouldPrintUserContent,
      pageRangesQueue,
      pageRangeStart,
      pageRangeEnd,
      hasPrintError,
      isCancelPrintDialogOpen,
    ],
  );

  const modifiers = useMemo(
    () => ({
      cancelPrintJob,

      setShouldPrintUserContent,

      setPageRangesQueue,
      setPageRangeStart,
      setPageRangeEnd,
      addPageRange,
      deleteQueue,

      setHasPrintError,
      retryPrintAction,
      closeErrorDialog,

      setIsCancelPrintDialogOpen,
      setIsCurrentPrintJobCanceled,

      onPrint,
      clearAllSelections,
    }),
    [
      cancelPrintJob,
      setShouldPrintUserContent,
      setPageRangesQueue,
      setPageRangeStart,
      setPageRangeEnd,
      addPageRange,
      deleteQueue,
      setHasPrintError,
      retryPrintAction,
      closeErrorDialog,
      onPrint,
      clearAllSelections,
      setIsCancelPrintDialogOpen,
      setIsCurrentPrintJobCanceled,
    ],
  );

  // For debug
  window.v7.print = {
    ...values,
    ...modifiers,
  };

  return (
    <PrintValuesContext.Provider value={values}>
      <PrintModifiersContext.Provider value={modifiers}>
        {children}
      </PrintModifiersContext.Provider>
    </PrintValuesContext.Provider>
  );
};

export default PrintProvider;
